import type { ReactNode } from "preact/compat";
import { createContext } from "preact/compat";
import { useEffect, useState, useContext, useMemo, useRef } from "preact/hooks";
import { trimStart } from "lodash-es";
import { backOff } from "exponential-backoff";
import type {
	Palette,
	BrickedPictureOutput,
	PictureConfiguration,
} from "@brickme/project-core/src/model/picture.ts";
import type { BuildSources } from "@brickme/project-core/src/image-build/create-bricked-image-from-source.ts";
import type { BitmapMask } from "@brickme/project-core/src/model/bitmap-mask.ts";
import { createBitmapMaskFromBitmap } from "@brickme/project-core/src/model/bitmap-mask.ts";
import { loadBitmapFromUrl } from "@brickme/project-core/src/bitmap/create.ts";
import { createPixAPicPalettes } from "@brickme/project-core/src/pix-a-pic.ts";
import type { SourceImage } from "@brickme/frontend-common/src/source-image/source-image.ts";
import { RenderModeProvider } from "@brickme/frontend-common/src/picture-render/render-mode-context.tsx";
import { PictureEditProvider } from "@brickme/frontend-common/src/picture-edit/picture-edit-context.tsx";
import { useMainRegionApiClient } from "~/hooks/region/context.tsx";
import uploadTempFile from "~/api/upload-temp-file.ts";
import type {
	StartBackgroundMaskJobMutation,
	StartBackgroundMaskJobMutationVariables,
} from "./start-background-mask-job-mutation.api.generated.ts";
import startBackgroundMaskJobMutation from "./start-background-mask-job-mutation.api.graphql";
import type {
	PublicFilterJobQuery,
	PublicFilterJobQueryVariables,
} from "./public-filter-job-query.api.generated.ts";
import publicFilterJobQuery from "./public-filter-job-query.api.graphql";
import { useBuildWorker } from "./worker-context.tsx";
import createDefaultPictureConfiguration from "./create-default-picture-configuration.ts";
import { BuildWhenRenderModeRequiresPictureBuildProvider } from "@brickme/frontend-common/src/picture-build/conditional-picture-build-context.tsx";

type PaletteValue = {
	readonly kitsPalette: Palette;
	readonly kitPlusExtrasPalette: Palette;
};

const paletteContext = createContext<PaletteValue | undefined>(undefined);

type BuildSourcesValue = {
	readonly sourceImage: SourceImage & {
		readonly mediaFileKey: string | undefined;
	};
	readonly backgroundMaskImage:
		| {
				readonly bitmap: BitmapMask;
				readonly mediaFileKey: string;
		  }
		| undefined;
};

const buildSourcesContext = createContext<BuildSourcesValue | undefined>(
	undefined,
);

type BackgroundMaskLoad =
	| {
			readonly type: "pending";
	  }
	| {
			readonly type: "loading";
			readonly publicFilterJobId: string;
	  }
	| {
			readonly type: "loaded";
			readonly result: {
				readonly bitmap: BitmapMask;
				readonly mediaFileKey: string;
			};
	  };

type EditorProviderProps = {
	readonly sourceImage: SourceImage;
	readonly numberOfKits: number;
	readonly systemPalette: Palette;
	readonly children: ReactNode;
};

function EditorProvider({
	sourceImage,
	numberOfKits,
	systemPalette,
	children,
}: EditorProviderProps) {
	// Palette
	const paletteValue = useMemo(() => {
		return createPixAPicPalettes(systemPalette, numberOfKits);
	}, [numberOfKits, systemPalette]);
	const { kitPlusExtrasPalette, kitsPalette } = paletteValue;

	// Kind of weird state that is in child providers. Could possibly solve by provider
	// ordering. Or need may be removed entirely once put all functionality into central
	// providers.
	const [brickedBuild, setBrickedBuild] = useState<
		BrickedPictureOutput | undefined
	>();

	// Saving source image
	const apiClient = useMainRegionApiClient();
	const [sourceImageKey, setSourceImageKey] = useState<string | undefined>();
	useEffect(() => {
		const abortController = new AbortController();
		(async () => {
			const newTempFileKey = await uploadTempFile(apiClient, sourceImage, {
				signal: abortController.signal,
			});
			setSourceImageKey(newTempFileKey);
		})();
		return () => {
			abortController.abort();
		};
	}, [apiClient, sourceImage]);

	// Fetch Background mask
	const [backgroundMask, setBackgroundMask] = useState<BackgroundMaskLoad>({
		type: "pending",
	});
	useEffect(() => {
		if (brickedBuild === undefined) {
			return;
		}

		if (backgroundMask.type === "loaded") {
			return;
		}

		const { operationsMissingSources } = brickedBuild;
		if (!operationsMissingSources.includes("Remove background")) {
			return;
		}

		if (backgroundMask.type === "pending") {
			if (!sourceImageKey) {
				return;
			}

			const abortController = new AbortController();
			(async () => {
				const { startBackgroundMaskJob } = await apiClient.mutate<
					StartBackgroundMaskJobMutation,
					StartBackgroundMaskJobMutationVariables
				>(
					startBackgroundMaskJobMutation,
					{
						sourceImageKey,
					},
					{
						signal: abortController.signal,
					},
				);

				setBackgroundMask({
					type: "loading",
					publicFilterJobId: startBackgroundMaskJob.id,
				});
			})();
			return () => {
				abortController.abort();
			};
		}

		if (backgroundMask.type === "loading") {
			const abortController = new AbortController();
			(async () => {
				const foundResultImageUrl = await backOff(
					async () => {
						const {
							publicFilterJob: { resultImageUrl },
						} = await apiClient.query<
							PublicFilterJobQuery,
							PublicFilterJobQueryVariables
						>(
							publicFilterJobQuery,
							{
								id: backgroundMask.publicFilterJobId,
							},
							{
								signal: abortController.signal,
							},
						);
						if (!resultImageUrl) {
							throw new Error("No result image url");
						}

						return resultImageUrl;
					},
					{
						delayFirstAttempt: false,
						jitter: "none",
						startingDelay: 500,
						maxDelay: 3_000,
						numOfAttempts: 100,
						timeMultiple: 1.5,
					},
				);

				const bitmap = await loadBitmapFromUrl(foundResultImageUrl);
				setBackgroundMask({
					type: "loaded",
					result: {
						bitmap: createBitmapMaskFromBitmap(bitmap),
						mediaFileKey: trimStart(new URL(foundResultImageUrl).pathname, "/"),
					},
				});
			})();
			return () => {
				abortController.abort();
			};
		}
	}, [apiClient, brickedBuild, backgroundMask, sourceImageKey]);
	const buildSourcesValue = useMemo(
		(): BuildSourcesValue => ({
			sourceImage: {
				...sourceImage,
				mediaFileKey: sourceImageKey,
			},
			backgroundMaskImage:
				backgroundMask.type === "loaded" ? backgroundMask.result : undefined,
		}),
		[sourceImage, backgroundMask, sourceImageKey],
	);

	// Send build sources to worker
	const buildSources = useMemo(
		(): BuildSources => ({
			original: sourceImage.bitmap,
			backgroundMask:
				backgroundMask.type === "loaded"
					? backgroundMask.result.bitmap
					: undefined,
			enhanceFaces: undefined,
			colorisation: undefined,
			facesMask: undefined,
			posterise: undefined,
		}),
		[sourceImage.bitmap, backgroundMask],
	);

	const buildWorker = useBuildWorker();

	function editCreateDefaultPictureConfiguration() {
		return createDefaultPictureConfiguration({ numberOfKits, kitsPalette });
	}
	const [picture, setPicture] = useState<PictureConfiguration>(
		editCreateDefaultPictureConfiguration,
	);
	const pictureRef = useRef(picture);
	pictureRef.current = picture;
	const pictureStorage = useMemo(() => {
		const changeListeners: (() => void)[] = [];
		return {
			getPicture() {
				return pictureRef.current;
			},
			setPicture(newPicture: Parameters<typeof setPicture>[0]) {
				setPicture(newPicture);
				for (const changeListener of changeListeners) {
					changeListener();
				}
			},
			addEventListener(_: "change", handler: () => void) {
				changeListeners.push(handler);
			},
			removeEventListener(_: "change", handler: () => void) {
				changeListeners.push(handler);
			},
		};
	}, []);

	return (
		<paletteContext.Provider value={paletteValue}>
			<buildSourcesContext.Provider value={buildSourcesValue}>
				<PictureEditProvider
					storage={pictureStorage}
					sourceImageDimensions={sourceImage.bitmap}
				>
					<RenderModeProvider>
						<BuildWhenRenderModeRequiresPictureBuildProvider
							buildSources={buildSources}
							palette={kitPlusExtrasPalette}
							backgroundImages={[]}
							buildWorker={buildWorker}
							onChange={setBrickedBuild}
						>
							{children}
						</BuildWhenRenderModeRequiresPictureBuildProvider>
					</RenderModeProvider>
				</PictureEditProvider>
			</buildSourcesContext.Provider>
		</paletteContext.Provider>
	);
}

function useSourceImageBitmap() {
	const context = useContext(buildSourcesContext);
	if (context === undefined) {
		throw new Error("useSourceImage must be used within context");
	}
	return context.sourceImage.bitmap;
}

function useBuildSourcesMediaKeys() {
	const context = useContext(buildSourcesContext);
	if (context === undefined) {
		throw new Error("useSourceImage must be used within context");
	}
	return {
		sourceImageKey: context.sourceImage.mediaFileKey,
		backgroundMaskImageKey: context.backgroundMaskImage?.mediaFileKey,
	};
}

function usePalettes() {
	const context = useContext(paletteContext);
	if (context === undefined) {
		throw new Error("usePalettes must be used within context");
	}
	return context;
}

export {
	useBuildSourcesMediaKeys,
	usePalettes,
	EditorProvider,
	useSourceImageBitmap,
};
